<template>
    <div class="row">
        <div class="col-12 mb-3">
            <a href="back" @click.prevent="goBack" class="link-hover-line text-sm text-default">
                <i class="fa fa-arrow-left mr-1"></i> Go back to service selection
            </a>
        </div>
        <div class="col-lg-8 col-sm-12">
            <div class="shadow-reg mb-0 bg-white">
                <shop-heading :shop="shop"/>
                
                <div class="heading-small pl-4 pr-4">Services Summary</div>
                <div class="container">
                    <div class="row mb-4 list-container">
                        <service-list-item v-for="(service, key) in services" :key="`service-list-item-${ key }`" :service="service" @update="showServiceForm" @remove="(service) => removeService(key, 'service')"/>
                        <service-list-item v-for="(_package, key) in packages" :key="`package-list-item-${ key }`" :service="_package" @update="showServiceForm" @remove="(service) => removeService(key, 'package')"/>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-lg-4 col-sm-12">
            <payment-section 
                ref="paymentSection"
                :points-used="form.points_used"
                :point-equivalent="bookingStats.pointEquivalent"
                :services="services"
                :packages="packages"
                :booking-voucher="form.voucher"
                :unsettled-penalties="bookingStats.unsettledPenalties"
                :is-requesting="isRequesting"
                @checkout="checkout"
            />
                
        </div>
        <div class="col-lg-8 col-sm-12">
            <card v-if="isLoggedIn" class="mb-5">
                <template slot="body">
                    <h6 class="heading-small text-muted montserrat">Payment Information</h6>
                    <div class="row">
                        <div v-if="!hasServiceVoucher" class="col-lg-6 col-sm-12 mb-5">
                            <modal-selector
                                title="Booking voucher"
                                label="Booking voucher"
                                placeholder="Booking voucher"
                                :fetch-link="voucherFetchLink"
                                :params="voucherParams"
                                paginate-path="data.result.vouchers"
                                v-model="form.voucher_id"
                                display-key="displayed_title"
                                sub-name-key="name"
                                ref="voucher"
                                @change="voucher => updateVoucher(voucher)"
                            >
                            </modal-selector>
                            <div class="row">
                                <div class="col-auto"><i class="fa fa-info-circle"></i></div>
                                <div class="col pl-0">
                                    <span class="text-muted">Discount applied to total service cost</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6 col-sm-12 mb-5">
                            <form-input type="integer" label="Points" :max="bookingStats.availablePoints" :disabled="bookingStats.availablePoints == 0" :placeholder="pointsFieldPlaceholder" show-optional-label :optional-label="availablePointsLabel" v-model="form.points_used"></form-input>
                            
                            <div class="row">
                                <div class="col-auto"><i class="fa fa-info-circle"></i></div>
                                <div class="col pl-0">
                                    <span class="text-muted">1 point is equivalent to PHP {{ bookingStats.pointEquivalent.toFixed(2) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6 col-sm-12 mb-5">
                            <form-select label="Payment type" placeholder="Payment type" v-model="form.payment_method" :options="paymentOptions"></form-select>
                        </div>
                    </div>
                </template>
            </card>
        </div>

        <service-modal ref="serviceModal" @update="updateService" />
    </div>
</template>
<script>
import '@styles/table.css';

import mixins from "@utils/mixins";
import { bus } from "@utils/bus";
import { route, get, post } from "@utils/network";
import { formatBookingCreationData } from "./helper";

import ShopHeading from "./ShopHeading";
import ServiceListItem from "./ServiceListItem";
import ServiceModal from "./ServiceModal";
import PaymentSection from "./PaymentSection";
import ModalSelector from "@components/forms/selects/modal-selector/ModalSelector";

export default {
    name: "CheckoutSection",

    components: { ShopHeading, ServiceListItem, ServiceModal, PaymentSection, ModalSelector },

    computed: {
        availablePointsLabel() {
            return `(${ parseFloat(this.bookingStats.availablePoints).toFixed(2) } points available)`;
        },

        pointsFieldPlaceholder() {
            return this.bookingStats.availablePoints == 0 ? "Insufficient points" : "Points";
        },

        pointDiscount() {
            return this.bookingStats.availablePoints * this.bookingStats.pointEquivalent;
        },

        shopSearchParams() {
            return this.$store.getters.shopSearchParams;
        },

        voucherParams() {
            return { type: "booking", shop_id: this.$store.getters.shop.id };
        },

        hasServiceVoucher() {
            const hasVoucher = service => service.voucher != undefined || service.voucher != null;
            return this.services.findIndex(hasVoucher) >= 0
                || this.packages.findIndex(hasVoucher) >= 0;
        },

        bookingStats() {
            return this.$store.getters.bookingStats;
        },

        isLoggedIn() {
            return this.$store.getters.userToken != null;
        },

        userToken() {
            return this.$store.getters.userToken;
        },

        shopParams() {
            const { is_home, datetime } = this.$store.getters.shopSearchParams;
            return { is_home, datetime };
        },

        paymentOptions() {
            return this.$store.getters.paymentOptions;
        }
    },

    data: () => ({
        voucherFetchLink: route("vouchers"),
        isRequesting: false,
        bookingVoucher: null,
        shop: {},
        address: {},
        services: [],
        packages: [],
        form: {
            voucher_id: null,
            voucher: null,
            datetime: null,
            points_used: 0,
            is_home: null,
            payment_method: "cash",
        }
    }),

    methods: {
        goBack() {
            this.$emit("previous");
        },

        initialize() {
            this.shop = this.$store.getters.shop;
            this.address = this.$store.getters.address;
            this.services = this.$store.getters.services || [];
            this.packages = this.$store.getters.packages || [];

            this.form.datetime = this.shopSearchParams.datetime;
            this.form.is_home = this.shopSearchParams.is_home;

            if (!this.isLoggedIn) {
                return setTimeout(() => this.requireLogin(), 1000);
            }
            this.fetchBookingStats();
        },

        updateVoucher(voucher) {
            this.form.voucher = voucher && voucher.id ? voucher : null;
            this.$nextTick(() => this.$refs.paymentSection.computeSummary());
        },

        showServiceForm(service) {
            const list = service.type == "package" ? "packages" : "services";
            const index = this[list].findIndex(item => item.id == service.id);

            if (index == -1) {
                return console.error("Service no found");
            }

            return this.$refs.serviceModal.show(this[list][index]);
        },

        updateService(service) {
            const list = service.type == "package" ? "packages" : "services";
            const index = this[list].findIndex(item => item.id == service.id);
            this[list][index] = {...service};

            bus.$emit(`service-updated-${ service.id }`, service);
            this.$refs.paymentSection.computeSummary();
        },

        removeService(index, type) {
            const list = type == "package" ? "packages" : "services";
            this[list].splice(index, 1);
            this.$refs.paymentSection.computeSummary();
        },

        fetchBookingStats() {
            this.$store.dispatch("getBookingStats")
                .catch(error => {
                    console.log(error);
                    this.promptErrors(error.response.data.message);
                });
        },

        async checkout() {
            if (!(await this.confirm("Are you sure you want to checkout this transaction?", "")).value) {
                return;
            }
            this.isRequesting = true;
            const data = formatBookingCreationData(this.shop.id, this.form, {services: this.services, packages: this.packages}, this.bookingVoucher, this.address);

            post(route("bookings"), { data })
                .then(response => this.checkedout(response))
                .catch(error => this.failedToCheckout(error))
                .finally(() => this.isRequesting = false);
        },

        checkedout(response) {
            const query = this.form.payment_method == "gcash" ? { initiate_payment: 1 } : {};
            this.promptMessage(response.data.message.message, "")
                .then(() => this.$router.push({
                    name: 'Booking Page',
                    params: { id: response.data.result.booking.id },
                    query
                }));
        },

        failedToCheckout(error) {
            console.log(error)
            this.promptFormErrors(this.$refs, error);
        },

        requireLogin() {
            localStorage.setItem("booking_cart", JSON.stringify({
                shop: this.$store.getters.shop,
                address: this.$store.getters.address,
                services: this.$store.getters.services,
                packages: this.$store.getters.packages,
                shopSearchParams: this.$store.getters.shopSearchParams,
            }));
            this.promptMessage("Log in required", "Please log in your account to proceed to checkout", "warning", false, {confirmButtonText: "Log in"})
                .then(() => this.$router.push({ name: 'Log in', query: { stat: "booking-checkout" } }));
        },
    },

    mixins: [mixins],

}
</script>